import { useRef } from 'react';
import { AnimationContextProvider } from '../hooks/useAnimationEnabled';
import { ExpenseOriginalPanel } from '../components/expenses/ExpenseOriginalPanel';
/**
 * T146 — /expenses/:id/edit.
 *
 * Full edit form — shares the `ExpenseForm` component with /expenses/new so
 * the line-item editor, totals, private-use override, and category/supplier
 * pickers stay in sync. Backend replaces line items atomically (see
 * updateExpense in apps/api/src/services/expense.ts).
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { LoadingSpinner, EmptyState, PageHeader } from '../components/ui';
import { ExpenseForm, type ExpenseFormSubmit } from '../components/expenses/ExpenseForm';
import { useExpense, useUpdateExpense } from '../services/api/expenses';
import { useToastHelpers } from '../components/ui/Toast';
import { expensePatch } from '../components/expenses/expense-draft';
import { markFormSaved } from '../hooks/useUnsavedChanges';
import { apiErrorMessage } from '../lib/api-error-message';
import { useClosedQuarterConfirmation } from '../hooks/useClosedQuarterConfirmation';

export const Route = createFileRoute('/expenses_/$id_/edit')({
  component: EditExpensePage,
});

function EditExpensePage() {
  const { id } = Route.useParams();
  return (
    <ProtectedRoute>
      <AnimationContextProvider context="detail-edit">
        <AppShell>
          <EditExpenseInner key={id} id={id} />
        </AppShell>
      </AnimationContextProvider>
    </ProtectedRoute>
  );
}

function EditExpenseInner({ id }: { id: string }) {
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const { data: expense, isLoading, error } = useExpense(id);
  const updateExpense = useUpdateExpense(id);
  const confirmation = useClosedQuarterConfirmation();
  const initialExpense = useRef(expense);
  if (expense && initialExpense.current?.id !== expense.id) initialExpense.current = expense;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (error || !expense) {
    return (
      <EmptyState
        title="Beleg nicht gefunden"
        description={(error as Error | undefined)?.message ?? undefined}
      />
    );
  }

  if (expense.status === 'archiviert') {
    return (
      <EmptyState
        title="Archivierter Beleg"
        description="Archivierte Belege können nicht mehr bearbeitet werden."
      />
    );
  }

  async function onSubmit(values: ExpenseFormSubmit) {
    if (!expense) return;
    const payload = expensePatch(initialExpense.current!, values) ?? {
      version: initialExpense.current!.version,
    };
    try {
      const saved = await confirmation.run((confirmed) =>
        updateExpense.mutateAsync({ ...payload, confirmNachträglich: confirmed })
      );
      if (!saved) return;
      markFormSaved();
      toast.success('Beleg aktualisiert');
      navigate({ to: '/expenses/$id', params: { id } });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  return (
    <>
      {/* 006b P1: Header gehört der Route (PATTERNS.md §3) */}
      <PageHeader backTo="/expenses" backLabel="Ausgaben" title="Beleg bearbeiten" />
      {confirmation.dialog}
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <ExpenseOriginalPanel expenseId={id} testid="expense-edit-original" />
        <div className="min-w-0">
          <ExpenseForm
            key={expense.id}
            mode="edit"
            initial={initialExpense.current!}
            onSubmit={onSubmit}
            submitLabel="Speichern"
            submitting={updateExpense.isPending}
            cancelHref={`/expenses/${id}`}
          />
        </div>
      </div>
    </>
  );
}
